import type { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import type { LoadingStep } from 'features/kestrelViewer/types/kestrelLoading'
import type { ParsedMessage } from 'features/kestrelViewer/utils/messageUtils'

// ============================================================================
// Connection Status
// ============================================================================

/**
 * The sequential states of a pixel streaming connection.
 * Each state represents a distinct phase in the connection lifecycle.
 */
export type ConnectionStatus =
  | 'idle' // No connection attempt in progress
  | 'creating-instance' // Creating PixelStreaming instance
  | 'connecting' // WebSocket connecting
  | 'authenticating' // Sending auth token, waiting for acceptance
  | 'waiting-for-streamer' // Authenticated, waiting for streamer list
  | 'ready' // Connected and streaming
  | 'error' // Unrecoverable error state
  | 'resetting' // Cleaning up before retry

/**
 * Maps connection status to the loading step shown in UI.
 * Some connection states don't have a direct UI representation.
 */
export const CONNECTION_STATUS_TO_LOADING_STEP: Readonly<
  Partial<Record<ConnectionStatus, LoadingStep>>
> = {
  'creating-instance': 'connecting',
  connecting: 'connecting',
  authenticating: 'authenticating',
  'waiting-for-streamer': 'loading-model',
  ready: 'initializing-scene',
}

// ============================================================================
// Connection State
// ============================================================================

/**
 * Full connection state including status and any associated data.
 */
export type ConnectionState =
  | { status: 'idle' }
  | { status: 'creating-instance' }
  | { status: 'connecting'; instance: PixelStreaming }
  | { status: 'authenticating'; instance: PixelStreaming }
  | { status: 'waiting-for-streamer'; instance: PixelStreaming }
  | { status: 'ready'; instance: PixelStreaming }
  | { status: 'error'; error: string; instance?: PixelStreaming }
  | { status: 'resetting' }

// ============================================================================
// Connection Configuration
// ============================================================================

/**
 * Configuration required to establish a pixel streaming connection.
 */
export interface ConnectionConfig {
  /** The signalling server WebSocket URL */
  url: string

  /** The video parent element to attach the stream to */
  videoParent: HTMLDivElement

  /** Authorization token (Bearer token, we'll extract just the token part) */
  authToken: string

  /** Whether we're in dev mode (skips auth/WebSocket patching) */
  devMode: boolean

  /** Timeout in ms for waiting for a streamer (default: 10000) */
  streamerTimeoutMs?: number

  /** The Kestrel model ID (for tracing) */
  kestrelModelId?: string

  /** The UUID of the provisioned VM (for tracing) */
  vmId?: string
}

// ============================================================================
// Event Types
// ============================================================================

/**
 * Event detail for state change events.
 */
export interface StateChangeEventDetail {
  state: ConnectionState
  previousState: ConnectionState
}

/**
 * Event detail for message events (raw WebRTC messages).
 */
export interface MessageEventDetail {
  message: ParsedMessage
}

/**
 * Event detail for error events.
 */
export interface ErrorEventDetail {
  error: string
  recoverable: boolean
}

/**
 * Event detail for reset request events.
 * Emitted when the connection manager determines a new session is needed.
 */
export interface ResetRequestEventDetail {
  reason: 'timeout' | 'subscribe-failed' | 'auth-failed'
}

/**
 * All event types emitted by the connection manager.
 */
export interface ConnectionManagerEventMap {
  stateChange: CustomEvent<StateChangeEventDetail>
  message: CustomEvent<MessageEventDetail>
  error: CustomEvent<ErrorEventDetail>
  resetRequest: CustomEvent<ResetRequestEventDetail>
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if the connection state has an active PixelStreaming instance.
 */
export function hasInstance(
  state: ConnectionState
): state is ConnectionState & { instance: PixelStreaming } {
  return 'instance' in state && state.instance !== undefined
}

/**
 * Check if the connection is in a connectable state (can accept connect()).
 */
export function isConnectable(state: ConnectionState): boolean {
  return state.status === 'idle' || state.status === 'error'
}

/**
 * Check if the connection is actively connecting (not idle, error, or ready).
 */
export function isConnecting(state: ConnectionState): boolean {
  return (
    state.status === 'creating-instance' ||
    state.status === 'connecting' ||
    state.status === 'authenticating' ||
    state.status === 'waiting-for-streamer'
  )
}
